import * as WebBrowser from 'expo-web-browser';
import {
  ActivityIcon,
  CalendarDaysIcon,
  CameraIcon,
  CheckCircle2Icon,
  PlugZapIcon,
  WatchIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { getApiUrl } from '@/lib/api-url';
import { authedFetch } from '@/lib/authed-fetch';
import { addEventToAppleCalendar, calendarEventPayload } from '@/lib/integrations/calendar-actions';
import {
  getIntegrationProviderConfig,
  type IntegrationProvider,
  type IntegrationProviderConfig,
  type IntegrationStatus,
} from '@/lib/integrations/types';
import type { Event } from '@/lib/events';

WebBrowser.maybeCompleteAuthSession();

type IntegrationBadgeProps = Readonly<{
  provider: IntegrationProvider;
  ownerQuery?: string;
  returnPath: string;
  mode?: 'settings' | 'event';
  event?: Event;
  timeZone?: string;
}>;

const PROVIDER_ICONS = {
  google_calendar: CalendarDaysIcon,
  apple_calendar: CalendarDaysIcon,
  instagram: CameraIcon,
  strava: ActivityIcon,
  garmin: WatchIcon,
} satisfies Record<IntegrationProvider, typeof CalendarDaysIcon>;

function withOwner(path: string, ownerQuery?: string): string {
  if (!ownerQuery) {
    return path;
  }
  return path.includes('?') ? `${path}&${ownerQuery}` : `${path}?${ownerQuery}`;
}

function normalizeStatus(
  provider: IntegrationProvider,
  value: Partial<IntegrationStatus>
): IntegrationStatus {
  const config = getIntegrationProviderConfig(provider);

  return {
    provider,
    connected: Boolean(value.connected),
    available: value.available ?? !config.unavailableMessage,
    label: value.label ?? value.username ?? value.displayName ?? null,
    message: value.message ?? config.unavailableMessage ?? null,
    username: value.username ?? null,
    displayName: value.displayName ?? null,
    biography: value.biography ?? null,
    profilePictureUrl: value.profilePictureUrl ?? null,
    website: value.website ?? null,
    followersCount: value.followersCount ?? null,
    mediaCount: value.mediaCount ?? null,
    externalUserId: value.externalUserId ?? null,
    tokenExpiresAt: value.tokenExpiresAt ?? null,
    connectedAt: value.connectedAt ?? null,
    scopes: value.scopes ?? [],
  };
}

async function fetchIntegrationStatus(
  provider: IntegrationProvider,
  ownerQuery?: string
): Promise<IntegrationStatus> {
  if (provider === 'apple_calendar') {
    return normalizeStatus(provider, {
      available: Platform.OS !== 'web',
      message:
        Platform.OS === 'web' ? 'Apple Calendar add-event is available in the native app.' : null,
    });
  }

  const response = await authedFetch(withOwner(`/api/integrations/${provider}`, ownerQuery));
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? 'Failed to load integration status.');
  }

  return normalizeStatus(provider, (await response.json()) as Partial<IntegrationStatus>);
}

function settingsLabel(config: IntegrationProviderConfig, connected: boolean | undefined): string {
  return connected ? `DISCONNECT ${config.shortTitle}` : `CONNECT ${config.shortTitle}`;
}

function settingsDetail(
  provider: IntegrationProvider,
  config: IntegrationProviderConfig,
  status: IntegrationStatus | null
): string {
  if (status?.message) {
    return status.message;
  }
  if (provider === 'strava') {
    return status?.connected
      ? 'Connected · disconnect to revoke access and delete connection data'
      : 'Account link only · no activities or routes imported';
  }
  return status?.connected
    ? (status.label ?? 'Connected')
    : (config.unavailableMessage ?? 'Not connected');
}

export function IntegrationBadge({
  provider,
  ownerQuery,
  returnPath,
  mode = 'settings',
  event,
  timeZone,
}: IntegrationBadgeProps) {
  const config = getIntegrationProviderConfig(provider);
  const [status, setStatus] = React.useState<IntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isActing, setIsActing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const IconComponent = PROVIDER_ICONS[provider] ?? PlugZapIcon;

  const loadStatus = React.useCallback(async () => {
    try {
      setStatus(await fetchIntegrationStatus(provider, ownerQuery));
    } catch (loadError) {
      setStatus(null);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load integration.');
    } finally {
      setIsLoading(false);
    }
  }, [ownerQuery, provider]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      void loadStatus();
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [loadStatus]);

  async function launchOAuth() {
    setNotice(null);
    const ownerScoped = withOwner(`/api/integrations/${provider}/authorize`, ownerQuery);
    const separator = ownerScoped.includes('?') ? '&' : '?';
    const authorizePath = `${ownerScoped}${separator}return_path=${encodeURIComponent(returnPath)}`;
    const authorizeUrl = getApiUrl(authorizePath);

    if (Platform.OS === 'web') {
      window.location.assign(authorizeUrl);
      return;
    }

    const result = await WebBrowser.openAuthSessionAsync(authorizeUrl, getApiUrl('/'));
    if (result.type === 'success') {
      await loadStatus();
    }
  }

  async function disconnect() {
    setIsActing(true);
    setError(null);
    setNotice(null);
    try {
      const response = await authedFetch(withOwner(`/api/integrations/${provider}`, ownerQuery), {
        method: 'DELETE',
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? 'Failed to disconnect.');
      }
      await loadStatus();
      if (provider === 'strava') {
        setNotice('Strava access revoked and connection data deleted.');
      }
    } catch (disconnectError) {
      setError(
        disconnectError instanceof Error ? disconnectError.message : 'Failed to disconnect.'
      );
    } finally {
      setIsActing(false);
    }
  }

  async function runEventAction() {
    if (!event) {
      return;
    }

    setIsActing(true);
    setError(null);
    try {
      if (provider === 'apple_calendar') {
        await addEventToAppleCalendar(event, timeZone);
        setStatus((current) =>
          normalizeStatus(provider, { ...current, connected: true, label: 'Device' })
        );
        return;
      }

      if (provider === 'google_calendar') {
        if (!status?.connected) {
          await launchOAuth();
          return;
        }

        const response = await authedFetch(
          withOwner('/api/integrations/google_calendar/events', ownerQuery),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(calendarEventPayload(event, timeZone)),
          }
        );

        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? 'Failed to add event.');
        }
      }
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Integration action failed.');
    } finally {
      setIsActing(false);
    }
  }

  async function handlePress() {
    if (mode === 'event') {
      await runEventAction();
      return;
    }

    if (provider === 'apple_calendar') {
      setError(null);
      setStatus(normalizeStatus(provider, { connected: Platform.OS !== 'web', label: 'Device' }));
      return;
    }

    if (status?.connected) {
      await disconnect();
      return;
    }

    await launchOAuth();
  }

  const connected = status?.connected;
  const available = status?.available ?? !config.unavailableMessage;
  let label = settingsLabel(config, connected);
  if (mode === 'event') {
    label =
      provider === 'google_calendar' && !connected
        ? `CONNECT ${config.shortTitle}`
        : `ADD ${config.shortTitle}`;
  }

  const detail = settingsDetail(provider, config, status);

  if (mode === 'event') {
    return (
      <EventIntegrationActionButton
        accessibilityLabel={label}
        available={available}
        error={error}
        isActing={isActing}
        isLoading={isLoading}
        label={config.shortTitle}
        onPress={handlePress}
      />
    );
  }

  return (
    <View className="gap-2">
      <Button
        variant={connected ? 'secondary' : 'default'}
        onPress={() => void handlePress()}
        disabled={isLoading || isActing || !available}
        className="h-auto min-h-12 justify-between px-4 py-3"
      >
        <View className="flex-row items-center gap-3">
          <Icon as={connected ? CheckCircle2Icon : IconComponent} className="size-4" />
          <View className="min-w-0 flex-1">
            <Text className="text-xs" numberOfLines={1}>
              {isLoading ? 'LOADING' : label}
            </Text>
            <Text className="text-xs opacity-80" numberOfLines={2}>
              {detail}
            </Text>
          </View>
          {isActing || isLoading ? <ActivityIndicator size="small" /> : null}
        </View>
      </Button>
      {provider === 'strava' ? (
        <Text variant="muted">
          6am stores your Strava authorization and athlete ID to maintain this account link. It does
          not import activities or routes. Disconnect to revoke access and delete the connection
          data.
        </Text>
      ) : null}
      {error ? (
        <Text className="text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}
      {notice ? <Text className="text-sm">{notice}</Text> : null}
    </View>
  );
}

function EventIntegrationActionButton({
  accessibilityLabel,
  available,
  error,
  isActing,
  isLoading,
  label,
  onPress,
}: Readonly<{
  accessibilityLabel: string;
  available: boolean;
  error: string | null;
  isActing: boolean;
  isLoading: boolean;
  label: string;
  onPress: () => Promise<void>;
}>) {
  return (
    <View className="min-w-0 flex-1 gap-1">
      <Button
        accessibilityLabel={accessibilityLabel}
        className="h-11 w-full border-border bg-muted px-2"
        disabled={isLoading || isActing || !available}
        onPress={() => void onPress()}
        size="sm"
        variant="ghost"
      >
        <Icon as={CalendarDaysIcon} className="size-4 text-foreground" />
        <Text className="text-foreground">{isLoading ? 'Loading' : label}</Text>
        {isActing || isLoading ? <ActivityIndicator size="small" /> : null}
      </Button>
      {error ? (
        <Text className="max-w-48 text-xs text-destructive" selectable>
          {error}
        </Text>
      ) : null}
    </View>
  );
}
